import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Box, Typography, Tooltip, IconButton, alpha } from '@mui/material';
import {
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  Public as PublicIcon,
  Settings as SettingsIcon,
  PostAdd as PostAddIcon,
  AccountCircle as AccountCircleIcon,
  Logout as LogoutIcon,
  LibraryBooks as LibraryIcon,
  CalendarMonth as CalendarIcon,
  AdminPanelSettings as AdminIcon,
  PhotoLibrary as GalleryIcon,
  AutoMode as AutoModeIcon,
  Groups as TeamIcon,
} from '@mui/icons-material';
import { designTokens as t } from '../theme';
import axios, { API, clearAuth, isAdmin, isTeamMember, hasPage } from '../api';

const adminSection = {
  title: 'Admin',
  items: [{ text: 'Utilisateurs', icon: <AdminIcon fontSize="small" />, path: '/admin' }],
};

// Navigation organisée en sections pour la lisibilité. `page` = clé de
// permission d'équipe (les membres ne voient que leurs pages autorisées).
const sections = [
  {
    title: 'Menu',
    items: [
      { text: 'Dashboard', icon: <DashboardIcon fontSize="small" />, path: '/', page: 'dashboard' },
      { text: 'Créer un post', icon: <PostAddIcon fontSize="small" />, path: '/post-creator', page: 'create' },
      { text: 'Scheduled Posts', icon: <CalendarIcon fontSize="small" />, path: '/scheduled', page: 'scheduled' },
      { text: 'Automatisation', icon: <AutoModeIcon fontSize="small" />, path: '/automation', page: 'automation' },
    ],
  },
  {
    title: 'Contenu',
    items: [
      { text: 'Comptes', icon: <PeopleIcon fontSize="small" />, path: '/sessions', page: 'accounts' },
      { text: 'Post Library', icon: <LibraryIcon fontSize="small" />, path: '/library', page: 'library' },
      { text: 'Gallery', icon: <GalleryIcon fontSize="small" />, path: '/gallery', page: 'gallery' },
    ],
  },
  {
    title: 'Configuration',
    items: [
      { text: 'Proxies', icon: <PublicIcon fontSize="small" />, path: '/proxies', page: 'proxies' },
      { text: 'Profils mobiles', icon: <SettingsIcon fontSize="small" />, path: '/profiles', page: 'profiles', adminOnly: true },
      { text: 'Groupes', icon: <AccountCircleIcon fontSize="small" />, path: '/account-profiles', page: 'groups' },
      { text: 'Team', icon: <TeamIcon fontSize="small" />, path: '/team', ownerOnly: true },
    ],
  },
];

// Filtre les sections selon les permissions du membre connecté.
function visibleSections() {
  const base = isAdmin() ? [...sections, adminSection] : sections;
  return base
    .map((s) => ({
      ...s,
      items: s.items.filter((it) => {
        if (it.adminOnly && !isAdmin()) return false;
        if (it.ownerOnly && isTeamMember()) return false;
        if (it.page && !hasPage(it.page)) return false;
        return true;
      }),
    }))
    .filter((s) => s.items.length > 0);
}

function NavItem({ item, active, onClick }) {
  return (
    <Box
      role="button"
      onClick={onClick}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.25,
        px: 1.25,
        py: 0.9,
        mx: 1,
        borderRadius: 2,
        cursor: 'pointer',
        userSelect: 'none',
        color: active ? t.foreground : t.muted,
        backgroundColor: active ? alpha('#ffffff', 0.06) : 'transparent',
        position: 'relative',
        transition: 'background-color .15s ease, color .15s ease',
        '&:hover': {
          backgroundColor: alpha('#ffffff', active ? 0.07 : 0.04),
          color: t.foreground,
        },
        '&::before': active
          ? {
              content: '""',
              position: 'absolute',
              left: -4,
              top: '50%',
              transform: 'translateY(-50%)',
              height: 18,
              width: 3,
              borderRadius: 4,
              backgroundColor: t.primary,
            }
          : {},
      }}
    >
      <Box sx={{ display: 'flex', color: active ? t.primary : 'inherit' }}>{item.icon}</Box>
      <Typography sx={{ fontSize: '0.875rem', fontWeight: active ? 600 : 500 }}>
        {item.text}
      </Typography>
    </Box>
  );
}

function Sidebar({ width = 264 }) {
  const navigate = useNavigate();
  const location = useLocation();

  const userEmail = localStorage.getItem('userEmail') || 'utilisateur';
  const userName = userEmail.split('@')[0];
  const initial = userName.charAt(0).toUpperCase();

  const handleLogout = async () => {
    try { await axios.post(`${API}/api/auth/logout`); } catch (e) { /* ignore */ }
    clearAuth();
    navigate('/login');
  };

  return (
    <Box
      component="aside"
      sx={{
        width,
        flexShrink: 0,
        position: 'fixed',
        top: 0,
        left: 0,
        height: '100vh',
        display: { xs: 'none', md: 'flex' },
        flexDirection: 'column',
        bgcolor: t.card,
        borderRight: `1px solid ${t.border}`,
        zIndex: 1200,
      }}
    >
      {/* Marque */}
      <Box sx={{ px: 2.5, py: 2.5, display: 'flex', alignItems: 'center', gap: 1.25 }}>
        <Box
          component="img"
          src={`${process.env.PUBLIC_URL}/favicon.png`}
          alt="Threads"
          sx={{
            width: 34,
            height: 34,
            objectFit: 'contain',
          }}
        />
        <Box>
          <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, lineHeight: 1.1, letterSpacing: '-0.01em' }}>
            Threads ERASER
          </Typography>
          <Typography sx={{ fontSize: '0.7rem', color: t.mutedFaint, fontWeight: 500 }}>
            v3.0 · Control Panel
          </Typography>
        </Box>
      </Box>

      <Box sx={{ height: '1px', bgcolor: t.border }} />

      {/* Navigation par sections */}
      <Box sx={{ py: 1.5, flexGrow: 1, overflowY: 'auto' }}>
        {visibleSections().map((section) => (
          <Box key={section.title} sx={{ mb: 1.5 }}>
            <Typography
              sx={{
                px: 2.5,
                pb: 0.75,
                fontSize: '0.65rem',
                fontWeight: 700,
                letterSpacing: '0.08em',
                color: t.mutedFaint,
                textTransform: 'uppercase',
              }}
            >
              {section.title}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
              {section.items.map((item) => (
                <NavItem
                  key={item.text}
                  item={item}
                  active={location.pathname === item.path}
                  onClick={() => navigate(item.path)}
                />
              ))}
            </Box>
          </Box>
        ))}
      </Box>

      {/* Pied : carte utilisateur + déconnexion */}
      <Box sx={{ height: '1px', bgcolor: t.border }} />
      <Box
        sx={{
          m: 1,
          p: 1,
          borderRadius: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 1.25,
          backgroundColor: alpha('#fff', 0.025),
          border: `1px solid ${t.border}`,
        }}
      >
        <Box
          sx={{
            width: 34,
            height: 34,
            flexShrink: 0,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: `linear-gradient(135deg, ${t.primary}, ${t.primaryHover})`,
            color: '#fff',
            fontSize: '0.85rem',
            fontWeight: 700,
          }}
        >
          {initial}
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography noWrap sx={{ fontSize: '0.8rem', fontWeight: 600, lineHeight: 1.2, textTransform: 'capitalize' }}>
            {userName}
          </Typography>
          <Typography noWrap sx={{ fontSize: '0.7rem', color: t.mutedFaint }}>
            {userEmail}
          </Typography>
        </Box>
        <Tooltip title="Se déconnecter" placement="top" arrow>
          <IconButton
            onClick={handleLogout}
            size="small"
            sx={{ color: t.muted, '&:hover': { color: '#fca5a5', backgroundColor: alpha('#ef4444', 0.1) } }}
          >
            <LogoutIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
}

export default Sidebar;
